import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCogs, FaSearch, FaUndo, FaTrash, FaCreditCard } from "react-icons/fa";
import Pagination from "Components/Pagination";
import CommonModal from "Components/CommonModal";
import { catchError, changeState, commonModalSetting } from "JS/common";
import {
  statusList,
  simpleStatusList,
  applicantType,
  paidStatusList,
} from "JS/array";
import { getAgenciesList, agenciesMultiDelete, getResizeFileDownload } from "JS/API";
import { useMarkMenuSeen } from "Contexts/NewCountContext";
import useQueryParams from "utils/useQueryParams";
import useSyncPageWithQuery from 'utils/useSyncPageWithQuery';
import $ from "jquery";
import { listPageStyles } from "utils/designSystem";

const Application = () => {
  const [resetTrigger, setResetTrigger] = useState(false);
  const { getQueryString, getQueryParamsObject, updateQueryParams, resetQueryParams } = useQueryParams();
  const [pageInfo, setPageInfo] = useState(() => {
    const queryParams = getQueryParamsObject();
    return {
      page: Number(queryParams.page) || 1,
      limit: 10,
      totalPage: 1,
    };
  });
  useSyncPageWithQuery(setPageInfo)
  const [searchData, setSearchData] = useState(() => {
    const queryParams = getQueryParamsObject();
    return {
      markType: queryParams.markType || "all",
      progress: queryParams.progress || "all",
      applicantType: queryParams.applicantType || "all",
      startDate: queryParams.startDate || "",
      endDate: queryParams.endDate || "",
      markName: queryParams.markName || "",
      user_id: queryParams.user_id || "",
      user_name: queryParams.user_name || "",
      sort: queryParams.sort || "apply_date_desc",
    }
  });
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const [list, setList] = useState([]);
  let prevent = false;
  const navigate = useNavigate();
  useMarkMenuSeen("agencies");
  // 체크된 항목 ID들을 저장하는 상태
  const [idArr, setIdArr] = useState([]);
  const [imageUrls, setImageUrls] = useState({});

  // 스타일 정의
  const styles = {
    container: listPageStyles.container,
    header: listPageStyles.header,
    title: listPageStyles.title,
    searchCard: listPageStyles.searchCard,
    searchRow: {
      display: 'flex',
      gap: '20px',
      marginBottom: '16px',
      flexWrap: 'wrap',
      alignItems: 'center'
    },
    searchField: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minWidth: '200px'
    },
    searchLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      minWidth: '80px',
      flexShrink: 0
    },
    searchInput: {
      flex: 1,
      padding: '8px 12px',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#1e293b'
    },
    searchSelect: {
      flex: 1,
      padding: '8px 12px',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      color: '#1e293b',
      cursor: 'pointer'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      marginTop: '16px'
    },
    searchButton: listPageStyles.searchButton,
    resetButton: listPageStyles.resetButton,
    deleteSection: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: '16px'
    },
    deleteButton: listPageStyles.deleteButton,
    tableContainer: listPageStyles.tableContainer,
    tableWrapper: listPageStyles.tableWrapper,
    table: listPageStyles.table,
    th: listPageStyles.th,
    td: listPageStyles.td,
    checkbox: listPageStyles.checkbox,
    checkboxLabel: listPageStyles.checkboxLabel,
    markImage: {
      width: '40px',
      height: '40px',
      objectFit: 'contain',
      borderRadius: '4px',
      border: '1px solid #e2e8f0'
    },
    markType: {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      textAlign: 'center'
    },
    typeText: {
      backgroundColor: '#dbeafe',
      color: '#1d4ed8'
    },
    typeFigure: {
      backgroundColor: '#dcfce7',
      color: '#16a34a'
    },
    typeCompound: {
      backgroundColor: '#fef3c7',
      color: '#d97706'
    },
    userInfo: listPageStyles.userInfo,
    userName: {
      fontWeight: '500',
      marginBottom: '2px'
    },
    userId: {
      fontSize: '12px',
      color: '#6b7280'
    },
    paidStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: '600'
    },
    statusPaid: {
      color: '#059669'
    },
    statusReady: {
      color: '#dc2626'
    },
    statusPending: {
      color: '#d97706'
    },
    progressStatus: {
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      textAlign: 'center'
    },
    statusProcessing: {
      backgroundColor: '#fef3c7',
      color: '#d97706'
    },
    statusCompleted: {
      backgroundColor: '#dcfce7',
      color: '#16a34a'
    },
    statusWaiting: {
      backgroundColor: '#fee2e2',
      color: '#dc2626'
    },
    priceText: {
      fontFamily: 'monospace',
      fontWeight: '600',
      color: '#059669'
    }
  };

  // 상표 유형별 스타일 반환
  const getMarkTypeStyle = (type) => {
    const baseStyle = styles.markType;
    switch (type) {
      case 'TEXT':
        return { ...baseStyle, ...styles.typeText };
      case 'FIGURE':
        return { ...baseStyle, ...styles.typeFigure };
      case 'COMPOUND':
        return { ...baseStyle, ...styles.typeCompound };
      default:
        return { ...baseStyle, ...styles.typeText };
    }
  };

  // 상표 유형 텍스트 반환
  const getMarkTypeText = (type) => {
    switch (type) {
      case 'TEXT':
        return '문자상표';
      case 'FIGURE':
        return '도형상표';
      case 'COMPOUND':
        return '복합상표';
      default:
        return '문자상표';
    }
  };

  // 결제 상태 스타일 반환
  const getPaidStatusStyle = (status) => {
    const baseStyle = styles.paidStatus;
    switch (status) {
      case 'paid':
        return { ...baseStyle, ...styles.statusPaid };
      case 'ready':
      case 'failed':
        return { ...baseStyle, ...styles.statusReady };
      case 'refund':
      case 'partial_refund':
      case 'canceled':
      case 'refund_request':
        return { ...baseStyle, color: '#d97706' };
      default:
        return { ...baseStyle, ...styles.statusPending };
    }
  };

  // 진행 상태 스타일 반환
  const getProgressStatusStyle = (status) => {
    const baseStyle = styles.progressStatus;
    if (status === 'FilingReq') {
      return { ...baseStyle, ...styles.statusWaiting };
    } else if (status === 'completed' || status === 'RegComplete') {
      return { ...baseStyle, ...styles.statusCompleted };
    } else if (status === 'refund' || status === 'CancelAll' || status === 'InvestCancel' || status === 'FilingCancel') {
      return { ...baseStyle, ...styles.statusWaiting };
    } else {
      return { ...baseStyle, ...styles.statusProcessing };
    }
  };

  const getList = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => (prevent = false), 200);
    const result = await getAgenciesList(pageInfo, searchData);

    if (typeof result === "object") {
      const data = result?.data?.data;
      setList(result?.data?.data);

      const imageFetches = data.map(async (item, index) => {
        let fileName = "";
        let originalName = "";

        if (item.mark_type === "FIGURE" || item.mark_type === "TEXT") {
          fileName = item.image_filename;
          originalName = item.image_original_filename;
        } else if (item.mark_type === "COMPOUND") {
          fileName = item.compound_image_filename;
          originalName = "복합상표";
        }

        if (fileName && originalName) {
          try {
            const url = await fetchUploadedImage(fileName, originalName);
            return { id: item.id, url };
          } catch (error) {
            console.error("이미지 URL 가져오기 오류:", error);
            return { id: item.id, url: null };
          }
        }

        return { id: item.id, url: null };
      });

      const resolvedImages = await Promise.all(imageFetches);

      const imageMap = {};
      resolvedImages.forEach(({ id, url }) => {
        if (url) imageMap[id] = url;
      });

      setImageUrls(imageMap);

      setPageInfo((prev) => {
        const clone = { ...prev };
        clone.totalPage = result?.data?.meta?.total_page;
        return clone;
      });
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  const renderList = () => {
    return list?.map(
      (
        {
          id,
          mark_name,
          mark_type,
          progress_status,
          created_at,
          merchant_price,
          paid_status,
          user_id,
          user_name,
        },
        idx
      ) => {
        const checkDragText = (e) => {
          const selection = window.getSelection();
          if (selection.toString().length > 0) {
            return;
          }
          navigate(`/agencies/${id}`);
        };

        // 체크박스 개별 선택
        const checkEach = (e) => {
          e.stopPropagation(); // 체크박스에서 모달 금지
          let all = $(".agencies-check").length;
          let checked = $(".agencies-check:checked").length;
          let arr = [];
          if (all !== checked) $(".agencies-all-check").prop("checked", false);
          else $(".agencies-all-check").prop("checked", true);
          for (let i = 0; i < all; i++) {
            if (document.getElementsByClassName("agencies-check")[i].checked) {
              const val =
                document.getElementsByClassName("agencies-check")[i].value;
              arr.push(val);
              setIdArr(arr);
            }
          }
        };

        return (
          <tr 
            key={id}
            onClick={() => checkDragText()}
            onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f8fafc'}
            onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
          >
            <td style={styles.td} onClick={(e) => e.stopPropagation()}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  className="agencies-check"
                  onChange={checkEach}
                  value={id}
                  style={styles.checkbox}
                />
              </label>
            </td>
            <td style={styles.td}>{id}</td>
            <td style={styles.td}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {imageUrls?.[id] && (
                  <img
                    src={imageUrls[id]}
                    alt="썸네일"
                    style={styles.markImage}
                  />
                )}
              </div>
            </td>
            <td style={styles.td}>
              <span style={getMarkTypeStyle(mark_type)}>
                {getMarkTypeText(mark_type)}
              </span>
            </td>
            <td style={styles.td}>
              <span style={styles.priceText}>
                {Number(merchant_price || 0).toLocaleString()}원
              </span>
            </td>
            <td style={styles.td}>
              <span style={getPaidStatusStyle(paid_status)}>
                <FaCreditCard size={12} />
                {paidStatusList[paid_status]}
              </span>
            </td>
            <td style={styles.td}>
              <span style={getProgressStatusStyle(progress_status)}>
                {statusList[progress_status]}
              </span>
            </td>
            <td style={styles.td}>
              <div style={styles.userInfo}>
                <div style={styles.userName}>{user_name}</div>
                <div style={styles.userId}>({user_id})</div>
              </div>
            </td>
            <td style={styles.td}>{created_at?.replaceAll("T", " ")}</td>
          </tr>
        );
      }
    );
  };

  const fetchUploadedImage = async (fileName, originalFileName) => {
    try {
      const response = await getResizeFileDownload(fileName, originalFileName);
      const base64 = btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      const mimeType = response.headers["content-type"];
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error("이미지 불러오기 실패:", error);
      return null;
    }
  };

  //= 대행 다중 삭제
  const deleteAgencies = async () => {
    const data = idArr;
    const result = await agenciesMultiDelete(data);
    if (typeof result === "object") {
      commonModalSetting(setAlertBox, true, "alert", "삭제되었습니다.");
      $(".agencies-all-check").prop("checked", false);
      $(".agencies-check").prop("checked", false);
      changeState(setPageInfo, "page", 1);
      setIdArr([]);
      setAlert("completeDelete");
      commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "정상적으로 삭제되었습니다."
      );
    } else return await catchError(result, navigate, setAlertBox, setAlert);
  };

  // 전체 선택
  const checkAll = () => {
    let arr = [];
    if ($(".agencies-all-check").is(":checked")) {
      $(".agencies-check").prop("checked", true);
      const all = $(".agencies-check").length;
      for (let i = 0; i < all; i++) {
        const val = document.getElementsByClassName("agencies-check")[i].value;
        arr.push(val);
        setIdArr(arr);
      }
    } else {
      $(".agencies-check").prop("checked", false);
      setIdArr([]);
    }
  };

  useEffect(() => {
    getList();
  }, [pageInfo.page, searchData.sort, resetTrigger]);

  useEffect(() => {
    document.title = "마크픽 관리자 > 등록 대행 관리";
  }, []);

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaCogs />
          APPLICATION-AGENCIES
        </h1>
      </div>

      {/* 검색 영역 */}
      <div style={styles.searchCard}>
        {/* 첫 번째 행 */}
        <div style={styles.searchRow}>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>상표 유형</label>
            <select
              value={searchData.markType}
              onChange={(e) =>
                changeState(setSearchData, "markType", e.target.value)
              }
              style={styles.searchSelect}
            >
              <option value="all">선택</option>
              <option value="text">문자상표</option>
              <option value="figure">도형상표</option>
              <option value="compound">복합상표</option>
            </select>
          </div>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>진행 상태</label>
            <select
              value={searchData.progress}
              onChange={(e) =>
                changeState(setSearchData, "progress", e.target.value)
              }
              style={styles.searchSelect}
            >
              <option value="all">선택</option>
              {Object.keys(simpleStatusList).map((key, idx) => {
                return (
                  <option key={key} value={key}>
                    {Object.values(simpleStatusList)[idx]}
                  </option>
                );
              })}
            </select>
          </div>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>출원인 유형</label>
            <select
              value={searchData.applicantType}
              onChange={(e) =>
                changeState(setSearchData, "applicantType", e.target.value)
              }
              style={styles.searchSelect}
            >
              <option value="all">선택</option>
              {Object.keys(applicantType).map((key, idx) => {
                return (
                  <option key={key} value={key}>
                    {Object.values(applicantType)[idx]}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* 두 번째 행 */}
        <div style={styles.searchRow}>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>출원인 이름</label>
            <input
              type="text"
              value={searchData.user_name}
              onChange={(e) =>
                changeState(setSearchData, "user_name", e.target.value)
              }
              style={styles.searchInput}
            />
          </div>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>출원인 아이디</label>
            <input
              type="text"
              value={searchData.user_id}
              onChange={(e) =>
                changeState(setSearchData, "user_id", e.target.value)
              }
              style={styles.searchInput}
            />
          </div>
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>출원 상표명</label>
            <input
              type="text"
              value={searchData.markName}
              onChange={(e) =>
                changeState(setSearchData, "markName", e.target.value)
              }
              style={styles.searchInput}
            />
          </div>
        </div>

        {/* 버튼 그룹 */}
        <div style={styles.buttonGroup}>
          <button
            style={styles.searchButton}
            onClick={() => {
              setPageInfo((prev) => ({
                ...prev,
                page: 1,
              }));
              getList();
              updateQueryParams({ ...searchData, page: 1 });
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            <FaSearch />
            검색
          </button>
          <button
            style={styles.resetButton}
            onClick={() => {
              setSearchData((prev) => {
                const clone = { ...prev };
                clone.markType = "all";
                clone.progress = "all";
                clone.applicantType = "all";
                clone.startDate = "";
                clone.endDate = "";
                clone.markName = "";
                clone.user_id = "";
                clone.user_name = "";
                return clone;
              });
              setPageInfo((prev) => ({
                ...prev,
                page: 1,
              }));
              resetQueryParams();
              setResetTrigger((prev) => {
                const queryString = getQueryString();
                if (!queryString) return prev
                return !prev
              })
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            <FaUndo />
            초기화
          </button>
        </div>
      </div>

      {/* 삭제 버튼 */}
      <div style={styles.deleteSection}>
        <button
          style={styles.deleteButton}
          onClick={() => {
            if (!idArr.length)
              return commonModalSetting(
                setAlertBox,
                true,
                "alert",
                "삭제할 글을 선택해 주세요."
              );
            else {
              setAlert("deleteConfirm");
              commonModalSetting(
                setAlertBox,
                true,
                "confirm",
                "삭제하면 다시 복구할 수 없습니다.<br/>정말 삭제하시겠습니까?"
              );
            }
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
        >
          <FaTrash />
          삭제하기
        </button>
      </div>

      {/* 테이블 */}
      <div style={styles.tableContainer}>
        <div style={styles.tableWrapper} className="table-wrapper">
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      className="agencies-all-check"
                      onChange={checkAll}
                      style={styles.checkbox}
                    />
                  </label>
                </th>
                <th style={styles.th}>No.</th>
                <th style={styles.th}>출원 상표명</th>
                <th style={styles.th}>상표 유형</th>
                <th style={styles.th}>결제 금액</th>
                <th style={styles.th}>결제 상태</th>
                <th style={styles.th}>진행 상태</th>
                <th style={styles.th}>사용자정보</th>
                <th style={styles.th}>신청일</th>
              </tr>
            </thead>
            <tbody>{renderList()}</tbody>
          </table>
        </div>
      </div>

      {/* 페이지네이션 */}
      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {/* 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "deleteConfirm") deleteAgencies();
            else if (alert === "completeDelete") getList();
            else if (alert === "logout") navigate("/");
            else return;
          }}
        />
      )}

      {/* 반응형 스타일 */}
      <style>{`
        @media (max-width: 768px) {
          .table-wrapper {
            -webkit-overflow-scrolling: touch;
          }
          
          .table-wrapper::-webkit-scrollbar {
            height: 8px;
          }
          
          .table-wrapper::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        }
      `}</style>
    </div>
  );
};

export default Application;